import { useHousehold } from "../../app/useHousehold";
import { useNow } from "../../ui/time";
import { remPx } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";

interface ClockConfig {
  showSeconds: boolean;
  showDate: boolean;
  tagline: string;
  style: "hero" | "card";
}

function Clock({ config, size }: WidgetProps<ClockConfig>) {
  const { timeZone, settings } = useHousehold();
  const now = useNow(config.showSeconds ? "second" : "minute");
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    second: config.showSeconds ? "2-digit" : undefined,
    hour12: !settings.clock24h,
  }).formatToParts(now);
  const time = parts
    .filter((p) => p.type !== "dayPeriod")
    .map((p) => p.value)
    .join("")
    .trim();
  const period = parts.find((p) => p.type === "dayPeriod")?.value;
  const date = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long", month: "long", day: "numeric" }).format(now);

  // Numerals sized from the tile so the clock reads across a room. A one-row
  // tile puts the date beside the time instead of under it.
  const inline = config.showDate && size.height < remPx() * 7.5 && size.width > remPx() * 18;
  const timePx = inline
    ? Math.max(24, Math.min(size.height * 0.62, size.width * 0.13))
    : Math.max(32, Math.min(size.height * (config.showDate ? 0.56 : 0.8), size.width * 0.19));
  const tagline = config.tagline.trim();
  const showTagline = tagline && size.width > 600 && size.width > timePx * 5.2;
  const [line1, ...rest] = tagline.split(/(?<=[.!?])\s+/);

  return (
    <div className={`clock clock-${config.style}`}>
      <div className={`clock-main ${inline ? "is-inline" : ""}`}>
        <div className="clock-time" style={{ fontSize: `${timePx}px` }}>
          <span>{time}</span>
          {period && <span className="clock-period">{period}</span>}
        </div>
        {config.showDate && (
          <div className="clock-date" style={{ fontSize: `${Math.max(15, timePx * 0.27)}px` }}>
            {date}
          </div>
        )}
      </div>
      {showTagline && (
        <div className="clock-tagline" style={{ fontSize: `${Math.max(18, timePx * 0.38)}px` }} aria-hidden="true">
          <span>{line1}</span>
          {rest.length > 0 && <span className="indent">{rest.join(" ")}</span>}
          <svg viewBox="0 0 200 14" preserveAspectRatio="none" className="brush">
            <path d="M4 10 C 50 3, 120 2, 196 6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  );
}

export const clockWidget: WidgetDefinition<ClockConfig> = {
  type: "clock",
  version: 1,
  name: "Clock & date",
  description: "Large time and date, readable across the room.",
  icon: "clock",
  category: "time",
  defaultSize: { w: 6, h: 2 },
  minSize: { w: 2, h: 1 },
  stackHeight: 130,
  defaults: { showSeconds: false, showDate: true, tagline: "Gather close. Shine bright.", style: "hero" },
  settings: [
    {
      key: "style",
      label: "Style",
      type: "select",
      options: [
        { value: "hero", label: "On the scenery" },
        { value: "card", label: "On a card" },
      ],
    },
    { key: "showDate", label: "Show date", type: "toggle" },
    { key: "showSeconds", label: "Show seconds", type: "toggle", help: "Updates every second; uses a little more power." },
    { key: "tagline", label: "Tagline", type: "text", maxLength: 60, placeholder: "Leave empty to hide", help: "Shown in handwriting beside the clock when there’s room." },
  ],
  chrome: (c) => (c.style === "hero" ? "bare" : "card"),
  Component: Clock,
};
